import { useLegalSettings } from '@/hooks/useLegalSettings';
import { Skeleton } from '@/components/ui/skeleton';
import PageHero from '@/components/layout/PageHero';

const PrivacyPolicyContent = () => {
  const { data: legalSettings, isLoading } = useLegalSettings();

  return (
    <div className="bg-brand-mist">
      <PageHero
        title="Privacy Policy"
        description="How WhatsGood HK handles account, listing, and contact information."
        contentClassName="py-12 md:py-16"
      />

      <section className="border-b border-brand-cloud bg-white px-4 py-10 md:py-14">
        <div className="mx-auto max-w-6xl">
          <h2 className="public-section-title">Privacy at WhatsGood HK</h2>
          <p className="mt-3 leading-7 text-brand-slate">
            This page explains what information we collect, how it is used, and how to contact us about your data.
          </p>
          <p className="mt-5 border-t border-brand-cloud pt-4 text-sm text-brand-slate">
            <span className="font-medium text-brand-ink">Last updated:</span>{" "}
            {isLoading ? <Skeleton className="inline-block h-4 w-32" /> : legalSettings?.last_updated_date}
          </p>
        </div>
      </section>

      <section className="px-4 py-12 md:py-16">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[minmax(0,0.25fr)_minmax(0,0.75fr)] lg:gap-12">
          <aside className="hidden lg:block">
            <div className="sticky top-24 border-t border-brand-cloud pt-5">
              <p className="public-card-title">Policy details</p>
              <p className="mt-2 text-sm leading-6 text-brand-slate">
                Account, listing, contact, and usage information.
              </p>
            </div>
          </aside>

          <article className="rounded-md border border-brand-cloud bg-white p-6 shadow-sm md:p-8">
            <div className="max-w-none text-brand-slate [&_h2:first-child]:mt-0 [&_h2]:mb-4 [&_h2]:mt-8 [&_h2]:font-heading [&_h2]:text-h3 [&_h2]:font-semibold [&_h2]:text-brand-ink [&_li]:leading-7 [&_p]:leading-7 [&_strong]:font-semibold [&_strong]:text-brand-ink">
              <h2>1. Introduction</h2>
              <p>
                Welcome to WhatsGood HK ("we," "our," "us"). We are committed to protecting your privacy. This Privacy
                Policy explains how we collect, use, disclose, and safeguard your information when you visit our website
                whatsgoodhk.com (the "Site"). Please read this privacy policy carefully. If you do not agree with the
                terms of this privacy policy, please do not access the site.
              </p>

              <h2>2. Information We Collect</h2>
              <p>
                We may collect information about you in a variety of ways. The information we may collect on the Site
                includes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Personal Data:</strong> Personally identifiable information, such as your name, email address,
                  and telephone number, that you voluntarily give to us when you register with the Site or when you
                  choose to participate in various activities related to the Site, such as submitting a listing or
                  posting a review.
                </li>
                <li>
                  <strong>User-Generated Content:</strong> We collect information that you provide as part of your
                  listings, reviews, and profile information. This includes descriptions, photos, and any other content
                  you submit.
                </li>
                <li>
                  <strong>Derivative Data:</strong> Information our servers automatically collect when you access the
                  Site, such as your IP address, your browser type, your operating system, your access times, and the
                  pages you have viewed directly before and after accessing the Site.
                </li>
              </ul>

              <h2>3. How We Use Your Information</h2>
              <p>
                Having accurate information about you permits us to provide you with a smooth, efficient, and customized
                experience. Specifically, we may use information collected about you via the Site to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Create and manage your account.</li>
                <li>Display your listings and reviews to other users.</li>
                <li>Email you regarding your account or order.</li>
                <li>Monitor and analyze usage and trends to improve your experience with the Site.</li>
                <li>Prevent fraudulent transactions, monitor against theft, and protect against criminal activity.</li>
                <li>Respond to your service requests.</li>
              </ul>

              <h2>4. Disclosure of Your Information</h2>
              <p>
                We may share information we have collected about you in certain situations. Your information may be
                disclosed as follows:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>By Law or to Protect Rights:</strong> If we believe the release of information about you is
                  necessary to respond to legal process, to investigate or remedy potential violations of our policies,
                  or to protect the rights, property, and safety of others, we may share your information as permitted or
                  required by any applicable law, rule, or regulation.
                </li>
                <li>
                  <strong>Third-Party Service Providers:</strong> We may share your information with third parties that
                  perform services for us or on our behalf, including data analysis, hosting services (like Supabase),
                  and customer service.
                </li>
                <li>
                  <strong>Publicly Visible Information:</strong> Information you post in listings and reviews is intended
                  for public consumption and will be visible to users of the Site.
                </li>
              </ul>

              <h2>5. Security of Your Information</h2>
              <p>
                We use administrative, technical, and physical security measures to help protect your personal
                information. While we have taken reasonable steps to secure the personal information you provide to us,
                please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method
                of data transmission can be guaranteed against any interception or other type of misuse.
              </p>

              <h2>6. Your Rights and Choices</h2>
              <p>
                You may at any time review or change the information in your account or terminate your account by logging
                into your account settings and updating your account, or by contacting us using the contact information
                provided below.
              </p>

              <h2>7. Contact Us</h2>
              <p>
                If you have questions or comments about this Privacy Policy, please contact us through the contact form
                available on our website.
              </p>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyContent;
